// The timepoint a scan's new Time series-NMP entry starts with. A scan never fills the timepoint itself: the user
// picks a new entry, and the timepoint goes in as a suggestion they can change.
//
// Ported from the SP Data app's `src/features/perfusion-scan/suggestTimepoint.ts`, whose tests come over as
// `__tests__/suggestTimepoint.test.ts`.

import type { FormField, FormFieldChoice } from './form';
import { liveField, TIMEPOINT_VARIABLE } from './scannedValues';
import { convertStringHoursToNumberHours } from '@/utils/generalUtils';

/** Seconds since the Unix epoch, the way a Date field stores a moment. */
export type UnixSeconds = number;

/** The form's live timepoint field, if it has one. */
export const timepointField = (form: { items?: readonly FormField[] }): FormField | undefined =>
  liveField(form, TIMEPOINT_VARIABLE);

/** The live choices of the form's timepoint field, none when the form has no live one. */
export const timepointChoices = (form: { items?: readonly FormField[] }): FormFieldChoice[] =>
  timepointField(form)?.options.choices?.filter(choice => !choice.archived) ?? [];

/** The baseline sample's timepoint, as spdata-api's perfusion snapshot names it. */
const BASELINE_TIMEPOINT = '-10 mins';

/**
 * Where a choice stands, in hours from the perfusion start: what the Warn rules' `hours` makes of it. `hours` reads
 * `-10 mins` as -1/6, but the Warn rules accept it from 0 to 0.5 h, so it stands at the start. A choice `hours` can't
 * read, such as `Perfusion end`, has no place and is never suggested.
 */
export const hoursOf = (choice: FormFieldChoice): number =>
  choice.value === BASELINE_TIMEPOINT ? 0 : convertStringHoursToNumberHours(String(choice.value));

/**
 * The timepoint choice nearest to the hours between the perfusion start and the scan, or null with no start or no
 * choice to snap to. The first of two equally near choices wins.
 */
export const suggestTimepoint = (
  scanCapturedAt: UnixSeconds,
  perfusionStart: UnixSeconds | undefined,
  choices: readonly FormFieldChoice[]
): FormFieldChoice | null => {
  if (perfusionStart === undefined) return null;
  const elapsed = (scanCapturedAt - perfusionStart) / 3600;
  let nearest: FormFieldChoice | null = null;
  let nearestDistance = Infinity;
  for (const choice of choices) {
    const hours = hoursOf(choice);
    if (Number.isNaN(hours)) continue;
    const distance = Math.abs(elapsed - hours);
    if (distance < nearestDistance) {
      nearest = choice;
      nearestDistance = distance;
    }
  }
  return nearest;
};

/**
 * When the scan was filmed, in seconds, or undefined when it doesn't say. A Perfusion Companion scan may not say, and
 * when it was stored can be hours later, so it gets no suggestion.
 */
export const capturedSeconds = (scan: { capturedAt?: Date }): UnixSeconds | undefined => {
  const milliseconds = scan.capturedAt?.getTime();
  return milliseconds === undefined || Number.isNaN(milliseconds) ? undefined : milliseconds / 1000;
};
